import {
  cricketNumbers,
  currentLegRound,
  formatDart,
  hintForState,
  isBullUpDecidingLeg,
  isCheckoutFrozen,
  isTeamFormat,
  sideLabel,
  sideOf,
  type ClientAction,
  type MatchState,
} from "@shared/index";
import { useEffect, useMemo, useState } from "react";
import DartPad from "../components/DartPad";
import JoinQr from "../components/JoinQr";
import LivePlayerName from "../components/LivePlayerName";
import Overlay, { type HighscoreBeat } from "../components/Overlay";
import { Scoreboard, StandingsStrip } from "../components/Scoreboard";
import TotalPad from "../components/TotalPad";
import { DesktopUpdateCompact } from "../components/DesktopUpdate";
import AdminLoginForm from "../components/AdminLoginForm";
import { boardNumberLabel, cricketGameLabel, GAME_LABELS } from "../lib/labels";
import { useAutodartsInput } from "../lib/useAutodarts";
import { adminApi, saveAdminToken } from "../lib/adminSession";
import { fetchStats } from "../lib/statsApi";

interface Props {
  match: MatchState;
  code: string;
  offline?: boolean;
  lanUrls: string[];
  joinUrl?: string | null;
  connected: boolean;
  sound: boolean;
  error: string | null;
  isHost?: boolean;
  apiBase?: string;
  origin?: string | null;
  adminToken?: string | null;
  onAction: (action: ClientAction) => void;
  onSound: (on: boolean) => void;
  onHome: () => void;
  onSetup: () => void;
  onAdminToken?: (token: string | null) => void;
  onOpenAdmin?: () => void;
}

export default function Match({
  match,
  code,
  offline,
  lanUrls,
  joinUrl,
  connected,
  sound,
  error,
  isHost = false,
  apiBase = "",
  origin = null,
  adminToken = null,
  onAction,
  onSound,
  onHome,
  onSetup,
  onAdminToken,
  onOpenAdmin,
}: Props) {
  const [settings, setSettings] = useState(false);
  const [showPad, setShowPad] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminBusy, setAdminBusy] = useState(false);
  const [highscoreBests, setHighscoreBests] = useState<Record<string, number>>({});
  const [inputSource, setInputSource] = useState<"pad" | "autodarts">(
    match.config.inputSource === "autodarts" ? "autodarts" : "pad",
  );
  const idx = match.currentLeg?.currentPlayerIndex ?? 0;
  const player = match.config.players[idx] ?? match.config.players[0] ?? { id: "", name: "—" };
  const visit = match.currentLeg?.currentVisit ?? [];
  const totalMode = match.config.inputMode === "total";
  const supportsTotal = match.config.gameType === "x01";
  const cricket = match.config.gameType === "cricket";
  const autodarts = inputSource === "autodarts";
  const hint = useMemo(() => hintForState(match), [match]);
  const frozenCheckout =
    match.config.gameType === "x01" &&
    match.config.teamPlay === "team" &&
    isCheckoutFrozen(match.currentLeg, idx);
  const teamish = isTeamFormat(match.config);
  const throwerSide = teamish ? sideLabel(match.config, sideOf(idx)) : null;
  const playing = match.status === "playing";
  const visitTotal = visit.reduce((s, d) => s + d.segment * d.multiplier, 0);
  const round = currentLegRound(match);
  const throwerLeg = match.currentLeg?.players[idx];
  const knockedIds = match.currentLeg?.knockedPlayerIds ?? [];
  const knockedNames = knockedIds
    .map((id) => match.config.players.find((p) => p.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const cricketTargets = cricket ? cricketNumbers(match.config.cricket) : undefined;
  const gameTitle =
    match.config.gameType === "cricket" ? cricketGameLabel(match.config.cricket.scoring) : GAME_LABELS[match.config.gameType];

  const sendDart = (dart: { segment: number; multiplier: 1 | 2 | 3 }) =>
    onAction({ type: "THROW_DART", playerId: player.id, dart });
  const autodartsUi = useAutodartsInput({
    enabled: Boolean(autodarts && playing && (isHost || offline)),
    url: match.config.autodartsUrl,
    playing,
    visitLength: visit.length,
    onDart: sendDart,
    onTakeout: () => {
      if (visit.length === 0 || visit.length >= 3) return;
      onAction({ type: "CONFIRM_VISIT", playerId: player.id });
    },
  });

  useEffect(() => {
    setInputSource(match.config.inputSource === "autodarts" ? "autodarts" : "pad");
    setShowPad(false);
  }, [match.id]);

  useEffect(() => {
    if (match.config.gameType !== "highscore") {
      setHighscoreBests({});
      return;
    }
    let cancelled = false;
    void fetchStats(apiBase, window.steeldartDesktop, origin)
      .then((data) => {
        if (cancelled) return;
        const next: Record<string, number> = {};
        for (const row of data.players ?? []) {
          if (row.player.id) next[row.player.id] = row.lifetime.highscoreBest ?? 0;
        }
        setHighscoreBests(next);
      })
      .catch(() => {
        if (!cancelled) setHighscoreBests({});
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, match.id, match.config.gameType, origin]);

  const highscoreBeats: HighscoreBeat[] = useMemo(() => {
    if (match.config.gameType !== "highscore" || match.status !== "matchOver") return [];
    return match.config.players.flatMap((p, i) => {
      const current = match.currentLeg?.players[i]?.highscoreScore ?? 0;
      const previous = p.id ? (highscoreBests[p.id] ?? 0) : 0;
      if (previous > 0 && current > previous) {
        return [{ playerId: p.id, name: p.name, previous, current }];
      }
      return [];
    });
  }, [highscoreBests, match]);

  async function loginAdmin() {
    setAdminBusy(true);
    setAdminError(null);
    const desktop = window.steeldartDesktop;
    try {
      if (desktop?.adminLogin) {
        const result = await desktop.adminLogin(adminPassword);
        if (!result.ok) {
          setAdminError(result.error);
          return;
        }
        saveAdminToken(result.token);
        onAdminToken?.(result.token);
        setAdminPassword("");
        setAdminOpen(false);
        setSettings(false);
        onOpenAdmin?.();
        return;
      }
      const res = await fetch(adminApi(apiBase, "/api/admin/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminPassword.trim() }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; token?: string };
      if (!res.ok || !data.ok || !data.token) {
        setAdminError("Passwort falsch");
        return;
      }
      saveAdminToken(data.token);
      onAdminToken?.(data.token);
      setAdminPassword("");
      setAdminOpen(false);
      setSettings(false);
      onOpenAdmin?.();
    } catch {
      setAdminError("Server nicht erreichbar.");
    } finally {
      setAdminBusy(false);
    }
  }

  function chooseSource(source: "pad" | "autodarts") {
    setInputSource(source);
    setShowPad(false);
  }

  return (
    <div
      className={`relative grid min-h-[100dvh] ${
        cricket
          ? "grid-rows-[auto_auto_minmax(0,1fr)_auto] overflow-hidden"
          : "grid-rows-[auto_auto_minmax(0,1fr)_auto]"
      } landscape:lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,42vw)] landscape:lg:grid-rows-[auto_auto_minmax(0,1fr)]`}
    >
      <header className="safe-pad flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 landscape:lg:col-span-2">
        <button className="text-xs font-bold uppercase tracking-widest text-slate-400" onClick={onHome}>
          {offline || isHost ? "Neues Spiel" : "Verlassen"}
        </button>
        <div className="min-w-0 flex-1 text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.35em] text-amber-glow">
            {gameTitle}
            {match.config.teamPlay === "league"
              ? " · Liga-Team"
              : match.config.teamPlay === "team"
                ? " · Team"
                : ""}
            {offline ? "" : ` · ${code}`}
          </div>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            {match.config.gameType !== "highscore" ? (
              <>
            <span className="rounded-xl bg-ink-800 px-3 py-1 text-sm font-bold text-slate-200">
              Satz {match.currentSet}
            </span>
            <span className="rounded-xl bg-ink-800 px-3 py-1 text-sm font-bold text-slate-200">
              Leg {match.currentLegInSet}
            </span>
              </>
            ) : null}
            <span className="rounded-xl bg-amber-glow px-4 py-1 font-display text-2xl leading-none text-ink-950 sm:text-3xl">
              Runde {round}
            </span>
            {match.config.gameType === "elimination" ? (
              <span className="rounded-xl bg-ink-800 px-3 py-1 text-sm font-bold text-slate-200">
                Ziel {match.config.elimination.target}
                {match.config.elimination.extreme ? " · Extreme" : ""}
              </span>
            ) : null}
            {match.config.gameType === "shanghai" ? (
              <span className="rounded-xl bg-ink-800 px-3 py-1 text-sm font-bold text-slate-200">
                Nächste Zahl {boardNumberLabel(throwerLeg?.nextTarget)}
              </span>
            ) : null}
            {match.config.gameType === "highscore" ? (
              <span className="rounded-xl bg-ink-800 px-3 py-1 text-sm font-bold text-slate-200">
                {throwerLeg?.highscoreScore ?? 0} Pkt
              </span>
            ) : null}
          </div>
          {connected ? null : <div className="mt-1 text-xs text-crimson">getrennt…</div>}
        </div>
        <div className="flex items-center gap-3">
          {joinUrl ? <JoinQr url={joinUrl} variant="header" /> : null}
          <button className="text-xs font-bold uppercase tracking-widest text-slate-400" onClick={() => setSettings(true)}>
            Menü
          </button>
        </div>
      </header>

      <StandingsStrip state={match} />

      <div className={`min-h-0 ${cricket ? "overflow-hidden" : ""}`}>
        <Scoreboard state={match} highscoreBests={highscoreBests} />
      </div>

      <div
        className={`safe-pad flex flex-col border-t border-white/10 bg-ink-900/90 p-3 landscape:lg:border-l landscape:lg:border-t-0 ${
          cricket ? "min-h-0" : ""
        }`}
      >
        <div className={`shrink-0 rounded-2xl border border-amber-glow/35 bg-amber-glow/10 px-3 ${cricket ? "mb-1.5 py-1.5" : "mb-3 py-2"}`}>
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.35em] text-amber-glow">Wirft</div>
              {throwerSide ? (
                <div className="truncate text-[11px] font-bold uppercase tracking-[0.16em] text-amber-glow/80">
                  {throwerSide}
                </div>
              ) : null}
              <LivePlayerName
                player={throwerSide ? { ...player, teamName: null } : player}
                nameClassName="truncate font-display text-2xl leading-none text-white sm:text-3xl"
              />
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Aufnahme</div>
              <div className="num-display text-4xl text-white">{visitTotal}</div>
              {match.config.gameType === "x01" && (
                <div className="text-sm text-amber-glow">Rest {match.currentLeg?.players[idx]?.remaining}</div>
              )}
              {match.config.gameType === "cricket" && match.config.cricket.scoring !== "noScore" && (
                <div className="text-sm text-amber-glow">Pkt {throwerLeg?.cricketScore ?? 0}</div>
              )}
              {match.config.gameType === "elimination" && (
                <div className="text-sm text-amber-glow">
                  Stand {throwerLeg?.remaining} / {match.config.elimination.target}
                </div>
              )}
              {match.config.gameType === "shanghai" && (
                <div className="text-sm text-amber-glow">
                  {throwerLeg?.shanghaiScore} Pkt · Zahl {boardNumberLabel(throwerLeg?.nextTarget)}
                </div>
              )}
              {match.config.gameType === "highscore" && (
                <div className="text-sm text-amber-glow">{throwerLeg?.highscoreScore ?? 0} Pkt</div>
              )}
            </div>
          </div>
          <div className={`mt-2 flex gap-2 ${cricket ? "mt-1.5" : "mt-2"}`}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`num-display min-w-[3.2rem] flex-1 rounded-xl bg-ink-700 px-2 text-center ${
                  cricket ? "py-1 text-xl" : "py-2 text-2xl"
                }`}
              >
                {visit[i] ? formatDart(visit[i]!) : "·"}
              </div>
            ))}
          </div>
          {match.currentLeg?.bustMessage && (
            <p className="mt-2 font-semibold text-crimson">{match.currentLeg.bustMessage}</p>
          )}
          {knockedNames.length > 0 && (
            <p className="mt-2 font-semibold text-crimson">
              {match.lastEvent ?? `${knockedNames.join(" und ")} zurück auf 0!`}
            </p>
          )}
          {frozenCheckout ? (
            <p className="mt-1 text-sm font-semibold text-crimson">
              Ausmachen gesperrt – Partner hat mehr Rest als jedes gegnerische Team
            </p>
          ) : hint && hint !== "Ausmachen gesperrt" ? (
            <p className="mt-1 text-sm font-semibold text-amber-glow">Checkout {hint}</p>
          ) : hint ? (
            <p className="mt-1 text-sm font-semibold text-crimson">{hint}</p>
          ) : null}
        </div>
        {isHost && playing && isBullUpDecidingLeg(match) && (
          <button
            type="button"
            className="mb-3 min-h-touch w-full rounded-2xl bg-ink-800 text-xs font-bold uppercase tracking-[0.22em] text-slate-400"
            onClick={() => onAction({ type: "REOPEN_BULL_UP" })}
          >
            Ausbullen ändern
          </button>
        )}
        {supportsTotal && (
          <div className="mb-2 grid grid-cols-2 gap-2">
            <button
              className={`min-h-touch rounded-2xl text-sm font-black uppercase tracking-wide ${
                !totalMode ? "bg-amber-glow text-ink-950" : "bg-ink-700 text-slate-300"
              }`}
              onClick={() => onAction({ type: "SET_INPUT_MODE", mode: "single" })}
            >
              Einzeleingabe
            </button>
            <button
              className={`min-h-touch rounded-2xl text-sm font-black uppercase tracking-wide ${
                totalMode ? "bg-amber-glow text-ink-950" : "bg-ink-700 text-slate-300"
              }`}
              onClick={() => onAction({ type: "SET_INPUT_MODE", mode: "total" })}
            >
              Gesamteingabe
            </button>
          </div>
        )}
        <div className="mb-1.5 grid shrink-0 grid-cols-2 gap-1.5">
          <button
            type="button"
            className={`rounded-xl px-3 py-1.5 text-xs font-black uppercase tracking-wide ${
              !autodarts ? "bg-amber-glow text-ink-950" : "bg-ink-700 text-slate-300"
            }`}
            onClick={() => chooseSource("pad")}
          >
            Tastatur
          </button>
          <button
            type="button"
            className={`rounded-xl px-3 py-1.5 text-xs font-black uppercase tracking-wide ${
              autodarts ? "bg-amber-glow text-ink-950" : "bg-ink-700 text-slate-300"
            }`}
            onClick={() => chooseSource("autodarts")}
          >
            Autodarts
          </button>
        </div>
        {autodarts && (
          <div
            className={`mb-1.5 shrink-0 rounded-2xl px-3 py-1.5 ${
              autodartsUi.reachable ? "bg-ink-800 text-slate-200" : "bg-crimson/20 text-crimson"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-black uppercase tracking-[0.2em]">Autodarts · {autodartsUi.label}</span>
              <button
                type="button"
                className="text-xs font-bold uppercase tracking-widest text-amber-glow"
                onClick={() => setShowPad((v) => !v)}
              >
                {showPad ? "Tastatur aus" : "Tastatur"}
              </button>
            </div>
            {autodartsUi.error && <p className="mt-1 text-xs">{autodartsUi.error}</p>}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto">
        {totalMode && supportsTotal && !autodarts ? (
          <TotalPad
            disabled={!playing}
            canUndo={match.canUndo}
            error={error}
            onUndo={() => onAction({ type: "UNDO" })}
            onSubmit={(total) => onAction({ type: "SET_VISIT_TOTAL", playerId: player.id, total })}
          />
        ) : autodarts && !showPad ? (
          <div className="grid grid-cols-2 gap-2">
            <button
              className="pad-btn bg-ink-800 text-slate-200 disabled:opacity-40"
              onClick={() => onAction({ type: "UNDO" })}
              disabled={!playing || !match.canUndo}
            >
              Rückgängig
            </button>
            <button
              className="pad-btn bg-emerald-500 text-ink-950 disabled:opacity-40"
              onClick={() => onAction({ type: "CONFIRM_VISIT", playerId: player.id })}
              disabled={!playing || visit.length === 0 || visit.length >= 3}
            >
              Fertig
            </button>
          </div>
        ) : (
          <DartPad
            disabled={!playing}
            canUndo={match.canUndo}
            canConfirm={playing && visit.length > 0 && visit.length < 3}
            cricketTargets={cricketTargets}
            onUndo={() => onAction({ type: "UNDO" })}
            onConfirm={() => onAction({ type: "CONFIRM_VISIT", playerId: player.id })}
            onDart={(dart) => onAction({ type: "THROW_DART", playerId: player.id, dart })}
          />
        )}
        </div>
      </div>

      {knockedNames.length > 0 && playing && (
        <div className="pointer-events-none fixed left-1/2 top-24 z-20 -translate-x-1/2 rounded-2xl border border-crimson/50 bg-ink-950/95 px-5 py-3 text-center shadow-glow">
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-crimson">Elimination</div>
          <div className="mt-1 font-display text-2xl text-white">{knockedNames.join(" · ")}</div>
          <div className="text-sm font-semibold text-crimson">zurück auf 0</div>
        </div>
      )}

      {error && (
        <div className="pointer-events-none fixed bottom-24 left-1/2 z-20 -translate-x-1/2 rounded-full bg-crimson px-4 py-2 text-sm font-bold">
          {error}
        </div>
      )}

      <Overlay
        match={match}
        isHost={isHost}
        highscoreBeats={highscoreBeats}
        onContinue={() => onAction({ type: "ACKNOWLEDGE" })}
        onSetStarter={(playerIndex) => onAction({ type: "SET_LEG_STARTER", playerIndex })}
        onRematch={(throwSequence) => onAction({ type: "REMATCH", throwSequence })}
        onSetup={onSetup}
        onUndo={() => onAction({ type: "UNDO" })}
      />

      {settings && (
        <div className="fixed inset-0 z-40 flex items-end bg-black/65 sm:items-center sm:justify-center">
          <div className="w-full max-w-md rounded-t-3xl border border-white/10 bg-ink-800 p-5 sm:rounded-3xl">
            <h3 className="font-display text-3xl uppercase">Einstellungen</h3>
            <label className="mt-4 flex min-h-touch items-center justify-between rounded-2xl bg-ink-700 px-4">
              <span>Klick-Sound</span>
              <input type="checkbox" checked={sound} onChange={(e) => onSound(e.target.checked)} />
            </label>
            {isHost && (
              <div className="mt-4">
                <div className="mb-2 text-sm text-slate-400">Eingabe</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`min-h-touch rounded-2xl text-sm font-bold ${
                      !autodarts ? "bg-amber-glow text-ink-950" : "bg-ink-700"
                    }`}
                    onClick={() => chooseSource("pad")}
                  >
                    Tastatur
                  </button>
                  <button
                    type="button"
                    className={`min-h-touch rounded-2xl text-sm font-bold ${
                      autodarts ? "bg-amber-glow text-ink-950" : "bg-ink-700"
                    }`}
                    onClick={() => chooseSource("autodarts")}
                  >
                    Autodarts
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Autodarts liest den Board Manager ({match.config.autodartsUrl}). Nur die Desktop-App / Pi kann die
                  Kamera im LAN erreichen.
                </p>
              </div>
            )}
            {lanUrls.length > 0 && (
              <div className="mt-4 text-sm text-slate-400">
                {offline
                  ? `Andere Geräte: ${lanUrls.join(" · ")}`
                  : `Raum-ID ${code}${lanUrls[0] ? ` · ${lanUrls.join(" · ")}` : ""}`}
              </div>
            )}
            {!offline && !lanUrls.length && (
              <div className="mt-4 text-sm text-slate-400">Raum-ID {code}</div>
            )}
            <DesktopUpdateCompact />
            <div className="mt-4">
              {adminToken ? (
                <button
                  type="button"
                  className="pad-btn w-full bg-ink-700"
                  onClick={() => {
                    setSettings(false);
                    onOpenAdmin?.();
                  }}
                >
                  Admin öffnen
                </button>
              ) : !adminOpen ? (
                <button
                  type="button"
                  className="w-full text-left text-xs tracking-wide text-slate-500 underline decoration-slate-700 underline-offset-4"
                  onClick={() => {
                    setAdminOpen(true);
                    setAdminError(null);
                  }}
                >
                  Admin
                </button>
              ) : (
                <AdminLoginForm
                  password={adminPassword}
                  onPassword={setAdminPassword}
                  onSubmit={() => void loginAdmin()}
                  onCancel={() => {
                    setAdminOpen(false);
                    setAdminError(null);
                    setAdminPassword("");
                  }}
                  busy={adminBusy}
                  error={adminError}
                  hint={window.steeldartDesktop ? "Online-Passwort oder lokales Admin-Passwort." : undefined}
                />
              )}
            </div>
            <button className="pad-btn mt-5 w-full bg-ink-600" onClick={() => setSettings(false)}>
              Schließen
            </button>
            <button className="pad-btn mt-2 w-full bg-ink-900 text-slate-300" onClick={onHome}>
              {offline || isHost ? "Neues Spiel einrichten" : "Zur Startseite"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
